/**
 * GlobalUnhandledJSErrorLogService
 *
 * This service sets up global error handlers for JavaScript errors and unhandled promise rejections.
 * It captures these errors at the window level and logs them using the CustomLoggerService.
 *
 * Usage:
 *   - Inject this service in your app's root component or module.
 *   - Call initGlobalErrorHandlers() during app initialization to start capturing errors.
 *
 * This helps in tracking unexpected errors and promise rejections in production apps.
 */
import { Injectable } from '@angular/core';
import { CustomLoggerService } from '@monorepo/services';

@Injectable({
  providedIn: 'root',
})
export class GlobalUnhandledJSErrorLogService {
  constructor(private customLoggerService: CustomLoggerService) {}

  initGlobalErrorHandlers() {
    window.onerror = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error,
    ): boolean => {
      this.customLoggerService.logUnhandledError(message?.toString(), error?.stack, {
        source: `window.onerror_${source}`,
        lineno,
        colno,
      });
      return true;
    };

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      this.customLoggerService.logUnhandledError(
        event.reason?.message || event.reason?.toString() || 'Unhandled Promise Rejection',
        event.reason?.stack,
        { source: 'unhandledrejection' },
      );
      event.preventDefault();
    });
  }
}
